<i18n locale="en">
{
  "content-cost": "This will use {proposedCampaignUsage} of your monthly {monthlyLimit} text message budget. You’ll have {remainingAfterProposedUsage} remaining for this month.",
  "error": "Failed to check usage limits",
  "would-exceed-limits-cost": "This would exceed your monthly {monthlyLimit} text message budget. You have used {messagesSentUsage} this month. Try scheduling for another month.",
  "first-monthly-campaign-exceeds-limit": "This SMS campaign will exceed your {monthlyLimit} monthly budget. You will not be able to send any additional one-time SMS campaigns this month.",
  "content": { 
    "firstNBlasts": {
      "usage": "This will use 1 of your {total} monthly text message blasts.",
      "remaining": "You’ll have {count} text message blast remaining for the month. | You’ll have {count} text message blasts remaining for the month."
    },
    "lastBlast": "This is your last text message blast for the month. You’ll be able to send {total} new text message blast starting {date}. | This is your last text message blast for the month. You’ll be able to send {total} new text message blasts starting {date}.",
    "noBlastsLeft": {
      "warning": "You’ve used all of your text message blasts for the month.",
      "reset": "You’ll be able to send {total} new text message blast starting {date}. | You’ll be able to send {total} new text message blasts starting {date}."
    }
  }
}
</i18n>

<template>
  <div v-if="status === 'loaded'">
    <div v-if="FeatureFlags.isEnabled('hermes.updated-sms-ui')">
      <OwnType
        v-if="smsBlastsLeft > 1"
        :text="`${t('content.firstNBlasts.usage', { total: smsBlastsAllowedPerMonth })} ${t('content.firstNBlasts.remaining', { count: smsBlastsLeft - 1 })}`"
      />

      <OwnType
        v-else-if="smsBlastsLeft === 1"
        :text="
          t('content.lastBlast', {
            total: smsBlastsAllowedPerMonth,
            date: resetDate,
          })
        "
      />

      <OwnType
        v-else
        :text="`${t('content.noBlastsLeft.warning')} ${t(
          'content.noBlastsLeft.reset',
          {
            total: smsBlastsAllowedPerMonth,
            date: resetDate,
          }
        )}`"
      />
    </div>
    <div v-else>
      <OwnType
        v-if="limitCheck?.firstMonthlySmsCampaign"
        :text="
          limitCheck?.wouldExceedLimits
            ? t('first-monthly-campaign-exceeds-limit', {
                monthlyLimit,
              })
            : t('content-cost', {
                proposedCampaignUsage,
                monthlyLimit,
                remainingAfterProposedUsage,
              })
        "
      />

      <OwnType
        v-else
        :text="
          limitCheck?.wouldExceedLimits
            ? t('would-exceed-limits-cost', {
                monthlyLimit,
                messagesSentUsage,
              })
            : t('content-cost', {
                proposedCampaignUsage,
                monthlyLimit,
                remainingAfterProposedUsage,
              })
        "
      />
    </div>
  </div>
  <div
    v-else-if="status === 'loading'"
    class="align-center flex-row justify-center"
  >
    <OwnProgressSpinner :size="42" />
  </div>
  <OwnType v-else :text="t('error')" />
</template>

<script lang="ts" setup>
import { Olympus } from '@owner/olympus-client'
import { addMonths, format, startOfMonth } from 'date-fns'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useApplicationScope } from '@/core/store'
import { CurrencyFormatter } from '@/format'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useMappedGetter } from '@/store'
import { OwnType } from '@/ui/OwnType'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

import { CampaignResource } from '../types'

const props = defineProps<{ executeAt: Date }>()
const emit = defineEmits<{
  (event: 'update', wouldExceedLimits: boolean): void
}>()

const { t } = useI18n()
const FeatureFlags = useFeatureFlags()

const status = ref<'loading' | 'error' | 'loaded'>('loading')
const limitCheck = ref<Olympus.Sms.LimitCheck | undefined>(undefined)
const otcSmsLimitCheck = ref<Olympus.Sms.OtcSmsLimitCheck | undefined>(
  undefined
)

const proposedCampaignUsage = computed<string>(() =>
  limitCheck.value ? CurrencyFormatter(limitCheck.value.consumedByCampaign) : ''
)
const monthlyLimit = computed<string>(() =>
  limitCheck.value
    ? CurrencyFormatter(limitCheck.value.monthlySmsCostLimit)
    : ''
)
const remainingAfterProposedUsage = computed<string>(() =>
  limitCheck.value
    ? CurrencyFormatter(limitCheck.value.remainingAfterCampaign)
    : ''
)
const messagesSentUsage = computed<string>(() =>
  limitCheck.value
    ? CurrencyFormatter(limitCheck.value.smsMessagesSentCost)
    : ''
)

const smsBlastsAllowedPerMonth = computed<number>(() =>
  otcSmsLimitCheck.value ? otcSmsLimitCheck.value.smsBlastsAllowedPerMonth : 0
)

const smsBlastsSentThisMonth = computed<number>(() =>
  otcSmsLimitCheck.value ? otcSmsLimitCheck.value.smsBlastsSentThisMonth : 0
)

const smsBlastsLeft = computed<number>(
  () => smsBlastsAllowedPerMonth.value - smsBlastsSentThisMonth.value
)

const resetDate = computed<string>(() => {
  const startOfNextMonth = startOfMonth(addMonths(props.executeAt, 1))

  return format(startOfNextMonth, 'MMMM d, yyyy')
})

const campaign = useMappedGetter<CampaignResource | undefined>(
  'textBuilder/currentResource'
)

const { activeBrandId } = useApplicationScope()

const checkCampaignUsageAgainstLimits = async () => {
  // Prevent users from bypassing the limit check by pretending that the limit would be exceeded until loading is complete
  emit('update', true)
  status.value = 'loading'
  try {
    if (FeatureFlags.isEnabled('hermes.updated-sms-ui')) {
      const checkOTCSmsLimitsResult =
        await OlympusClient.dashboard.v1.brands.brand.campaigns.checkOTCSmsLimits(
          {
            params: { brandId: activeBrandId.value },
            query: {
              date: (props.executeAt ?? new Date()).toISOString(),
            },
          }
        )
      otcSmsLimitCheck.value = checkOTCSmsLimitsResult

      if (smsBlastsLeft.value > 0) {
        emit('update', false)
      } else {
        emit('update', true)
      }
    } else {
      if (!campaign.value?.id) {
        return undefined
      }

      // TODO(OSG-1456): remove else block once new UI is launched
      const checkLimitsResult =
        await OlympusClient.dashboard.v1.campaigns.campaign.checkLimits({
          params: { campaignId: campaign.value.id },
          query: {
            month: (props.executeAt ?? new Date()).toISOString(),
          },
        })
      limitCheck.value = checkLimitsResult

      if (checkLimitsResult.firstMonthlySmsCampaign) {
        emit('update', false)
      } else {
        emit('update', checkLimitsResult.wouldExceedLimits)
      }
    }

    status.value = 'loaded'
  } catch (err) {
    logError('Failed to check campaign usage against limits')
    status.value = 'error'
  }
}

onMounted(checkCampaignUsageAgainstLimits)
watch(() => props.executeAt, checkCampaignUsageAgainstLimits)
</script>
